import { useLocation } from "react-router-dom";

/*
 * Several pages — shooter profiles, competition/event/match results, target maps
 * — are reachable from both the public site and the admin panel. They are
 * mounted under both route trees, so a link inside them must stay in whichever
 * portal the user is already in: clicking a shooter from the admin panel should
 * keep the admin sidebar, not drop the administrator onto the public site.
 *
 *   const link = usePortalLink();
 *   <Link to={link(`/shooters/${sid}`)} />   // "/shooters/1" or "/admin/shooters/1"
 *
 * Not every public route has an admin twin, though. Blindly prefixing produced
 * dead links — the shooter page's "Shooters" breadcrumb pointed at /rankings,
 * and /admin/rankings does not exist, so it 404'd. Anything not in ADMIN_ROUTES
 * below is either redirected to its admin equivalent or left as a public link.
 */

/** Public path prefixes that are also mounted under /admin (see App.tsx). */
const ADMIN_ROUTES = [/^\/shooters\/[^/]+$/, /^\/strings\/[^/]+$/, /^\/competitions(\/|$)/];

/**
 * Public paths with a differently-named admin screen. The shooter register is
 * "Competitors" in the admin panel and "Rankings" on the public site, so the
 * same conceptual link resolves to a different route in each.
 */
const ADMIN_EQUIVALENT: Record<string, string> = {
  "/": "/admin",
  "/rankings": "/admin/competitors",
  "/rankings/class": "/admin/classes",
  "/results": "/admin/competitions",
  "/import-history": "/admin/import-history",
};

export function usePortalLink() {
  const inAdmin = useLocation().pathname.startsWith("/admin");
  return (path: string): string => {
    if (!inAdmin) return path;
    if (ADMIN_EQUIVALENT[path]) return ADMIN_EQUIVALENT[path];
    if (ADMIN_ROUTES.some((re) => re.test(path))) return `/admin${path}`;
    // No admin screen for this one — leave it pointing at the public site
    // rather than manufacturing a URL that does not resolve.
    return path;
  };
}

/** True when the current route is inside the admin panel. */
export function useIsAdminArea() {
  return useLocation().pathname.startsWith("/admin");
}
